import type { AuthService } from './auth'

export abstract class JBGApi {
    private baseUrl: string
    private auth: AuthService | undefined
    constructor(baseUrl: string, authService?: AuthService) {
        this.baseUrl = baseUrl.replace(/\/$/, '') // trim trailing slash
        this.auth = authService
    }
    // getUrl trims preceding slash
    private getUrl(route: string): string {
        return `${this.baseUrl}/${route.replace(/^\//, '')}`
    }

    // eslint-disable-next-line complexity
    public async callAPI<T>(route: string, options: RequestInit | undefined, refreshToken = true): Promise<T> {
        const url = this.getUrl(route)
        const response = await $fetch.native(url, options)
        let isJsonRes = false
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.indexOf('application/json') !== -1) {
            isJsonRes = true
        }
        // bad request, should have a `code` status to let the FE know how to handle
        if (response.status === 400) {
            const err = await response.json() as JBGApi.Error
            err.httpStatus = response.status
            if (isJsonRes) throw new JBGApi.Error(err.error, err.code, err.httpStatus)
            throw new Error('bad request')
        }
        // unauthorized, try to refresh
        if (response.status === 401) {
            if (this.auth && refreshToken) {
                await this.auth?.refreshToken()
                return this.callAPI(route, options, false)
            }
            if (isJsonRes) {
                const err = await response.json() as JBGApi.Error
                err.httpStatus = response.status
                throw new JBGApi.Error('unauthorized', err.code, err.httpStatus)
            } else {
                throw new JBGApi.Error('unauthorized', undefined, response.status as number)
            }
        }
        // successful
        if (response.status >= 200 && response.status < 300) {
            if (!isJsonRes || response.status === 201 || response.status === 202 || response.status === 204) {
                return response as T
            }
            const json = await response.json()
            return json
        }
        // all other requests
        if (isJsonRes) {
            const err = await response.json() as JBGApi.Error
            err.httpStatus = response.status
            throw new JBGApi.Error(err.error, err.code, err.httpStatus)
        }
        console.error('api internal service error')
        throw new Error()
    }

    public buildOptions(method: string, payload: any = null): RequestInit {
        const headers: HeadersInit = new Headers()
        headers.set('Accept', 'application/json')
        if (method.toLowerCase() !== 'get') {
            headers.set('Content-Type', 'application/json')
        }

        const options: RequestInit = {
            method: method.toUpperCase(),
            headers,
            mode: 'cors',
            credentials: 'include'
        }

        if (payload) {
            options.body = JSON.stringify(payload)
        }

        return options
    }
}

export namespace JBGApi {
    // Mirrors binjpipe internal/services/service.go, keep in sync
    export const ErrorCode = {
        Unknown: 0,
        // General errors
        BadRequest: 1000,
        InternalServer: 1001,
        InvalidURL: 1002,
        // User errors
        UserGeneral: 2000,
        UserNoEmailOrPW: 2001,
        UserPasswordNotMatch: 2002,
        UserEmailExistsInCognito: 2003,
        UserNotConfirmed: 2004,
        UserJBGExists: 2005,
        UserJBGNotExists: 2006,
        UserInvalidDOB: 2007,
        UserInvalidUsernameLen: 2008,
        UserInvalidUsername: 2009,
        UserMissingProfileFields: 2010,
        UserInappropriateUsername: 2011,
        UserExpiredVerificationCode: 2012,
        UserAlreadyReceivedFreeGameCode: 2013,
        UserNoEmailOrSteamId: 2014,
        UserInvalidEmailFormat: 2015,
        UserInvalidSteamID: 2016,
        UserInvalidVerificationCode: 2017,
        UserHasActiveSubscription: 2018,
        UserAlreadyHasPassword: 2019,
        UserUnderageDOB: 2020,
        // Auth errors
        AuthGeneral: 3000,
        AuthInvalidCookie: 3001,
        AuthInvalidSteamTicket: 3002,
        AuthInvalidSteamIdentity: 3003,
        AuthCodeMismatch: 3004,
        AuthExpiredCode: 3005,
        AuthLimitExceeded: 3006,
        AuthInvalidDeviceID: 3007,
        AuthInvalidDeviceCode: 3008,
        AuthInvalidDeviceSessionToken: 3009,
        AuthDeviceSessionExpired: 3010,
        AuthDeviceSessionNotClaimed: 3011,
        AuthDeviceSessionConsumed: 3012,
        AuthInvalidPassword: 3013,
        AuthInvalidProvider: 3014,
        AuthInvalidHandoffToken: 3015,
        AuthHandoffSessionExpired: 3016,
        AuthHandoffSessionConsumed: 3017,
        AuthInvalidHeader: 3018,
        AuthLinkEmailUnverified: 3019,
        AuthLinkProviderUntrusted: 3020,
        AuthLinkAccountUnconfirmed: 3021,
        // Gamestream errors
        GameGeneral: 4000,
        GameStreamAlreadyActive: 4001,
        // ABTest errors
        TestGeneral: 5000,
        // Game errors
        GameRetrievalSteam: 6000,
        GameCacheSteam: 6001,
        // Game stream group errors
        GameStreamGroupIdInvalid: 6002,
        GameStreamGroupRegionInvalid: 6003,
        GameStreamGroupUserNameInvalid: 6004
    } as const

    export class Error {
        public code: number | undefined
        public error: string
        public httpStatus: number | undefined
        constructor(error: string, code?: number, httpStatus?: number) {
            this.error = error
            this.code = code
            this.httpStatus = httpStatus
        }
    }

    export interface PagedResult<T> {
        data: T[]
        total_items: number
        total_pages: number
        page: number
        size: number
    }
}
